import { NpcBlockPanel } from '@/components/admin/npc/npc-block-panel';
import { NpcSourcesEditor } from '@/components/admin/npc/npc-sources-editor';
import { RequiredMark } from '@/components/admin/required-mark';
import { type NpcDraft, withBehaviourToggled } from '@/lib/npc-draft';
import { NPC_EXPECTED_OPTIONS, counterLabel } from '@/lib/npc-labels';
import type { NpcBehaviour, NpcKind, ReplaySettings } from '@matter_extractor/shared';
import {
  NPC_BEHAVIOURS,
  NPC_BEHAVIOUR_LABELS,
  NPC_FIRST_NAME_MAX,
  NPC_HOW_TO_PLAY_MAX,
  NPC_KIND_LABELS,
  NPC_MOMENT_MAX,
  NPC_NAME_MAX,
  NPC_WHO_MAX,
  npcKindSchema,
} from '@matter_extractor/shared';
import { TextArea, TextField } from '@pms-digital/design-system';
import { type ReactNode, useId } from 'react';

/**
 * The NPC's form, in the order of npc.md §3.1 (maquette `Fiche`, planche 1): name, first name,
 * kind, who, how to play, sources, what it goes through, expected noyau - then, read only, what the
 * player will receive.
 *
 * AUTONOMOUS, like the persona's: it holds no query and no mutation, so the page of a fiche, the
 * empty one and the one prefilled from a session mount the same fields. The two things that need a
 * read - the rules of Réglages > Rejeu and the link to the session an NPC came from - come in as
 * props and as a node (`originNote`).
 *
 * Every bound is the contract's (H27) and held by the field: `maxLength`, with a counter under the
 * two long ones. Read only (« Voir », a retired NPC) means `readOnly` and `aria-disabled`-free: the
 * text of a four-thousand-character instruction must stay selectable.
 *
 * The text fields are the package's `TextField` and `TextArea`, which fill their envelope: a field
 * narrower than the column (the first name, 240 px) states its width on a wrapping `<div>`.
 */

const KIND_BLURB: Readonly<Record<NpcKind, string>> = {
  famous: 'Ses propres mots, dits à l’oral.',
  written: 'Une personne qui n’existe pas, écrite pour un comportement.',
  session: 'Le transcript d’une session finie, avec son lien.',
};

export interface NpcFieldsProps {
  draft: NpcDraft;
  readOnly: boolean;
  /** The refusal of the rename (`name-taken`), shown under the name field. */
  nameError: string | null;
  /** The rules of Réglages > Rejeu, for « Ce que le joueur recevra » (see `NpcBlockPanel`). */
  settings: ReplaySettings | null | undefined;
  /** Under the kind: « Née de la session ... », built by the page that can link to it. */
  originNote?: ReactNode;
  onDraft: (next: NpcDraft) => void;
  onNameCommit: () => void;
}

function Counter({ used, max, testId }: { used: number; max: number; testId: string }) {
  return (
    <p data-testid={testId} className="text-right text-caption text-gray-500 leading-4">
      {counterLabel(used, max)}
    </p>
  );
}

export function NpcFields({
  draft,
  readOnly,
  nameError,
  settings,
  originNote,
  onDraft,
  onNameCommit,
}: NpcFieldsProps) {
  const kindName = useId();
  const expectedName = useId();

  return (
    <div className="min-w-0 space-y-7">
      <section>
        {/* The hint or the refusal, never neither: the package field points its
            describedby at whichever it shows. */}
        <TextField
          label="Nom du NPC"
          data-testid="npc-nom"
          value={draft.name}
          readOnly={readOnly}
          required
          aria-required="true"
          maxLength={NPC_NAME_MAX}
          placeholder="Un nom unique parmi les NPC actifs"
          onChange={(event) => onDraft({ ...draft, name: event.currentTarget.value })}
          onBlur={onNameCommit}
          help="Unique parmi les NPC actifs. C’est le nom côté opérateur : le joueur ne le reçoit pas."
          status={nameError === null ? 'default' : 'error'}
          message={nameError ?? undefined}
        />
      </section>

      <section>
        <div className="w-60 max-w-full">
          <TextField
            label="Prénom"
            data-testid="npc-prenom"
            value={draft.firstName}
            readOnly={readOnly}
            required
            aria-required="true"
            maxLength={NPC_FIRST_NAME_MAX}
            onChange={(event) => onDraft({ ...draft, firstName: event.currentTarget.value })}
            help="Ce que le joueur saisit aux pages, au champ « Votre prénom »."
          />
        </div>
      </section>

      <fieldset className="min-w-0 space-y-2 border-0 p-0">
        <legend className="mb-1 block font-semibold text-gray-800 text-small">
          Sorte
          <RequiredMark />
        </legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {npcKindSchema.options.map((kind) => {
            const checked = draft.kind === kind;
            return (
              <label
                key={kind}
                data-testid={`npc-sorte-${kind}`}
                data-checked={checked}
                className={`flex flex-col gap-1 rounded-md border-2 px-3.5 py-3 ${
                  checked ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-200 bg-white'
                } ${readOnly ? '' : 'cursor-pointer hover:border-gray-300'}`}
              >
                <span className="flex items-center gap-2 font-semibold text-gray-900 text-small">
                  <input
                    type="radio"
                    name={kindName}
                    value={kind}
                    checked={checked}
                    disabled={readOnly && !checked}
                    onChange={() => {
                      if (!readOnly) onDraft({ ...draft, kind });
                    }}
                    className="size-4 accent-bleu-nuit"
                  />
                  {kind === 'famous' ? 'Célèbre du domaine public' : NPC_KIND_LABELS[kind]}
                </span>
                <span className="text-caption text-gray-600 leading-4">{KIND_BLURB[kind]}</span>
              </label>
            );
          })}
        </div>
        {originNote}
      </fieldset>

      <section>
        <TextField
          label="Qui c’est"
          data-testid="npc-qui"
          value={draft.who}
          readOnly={readOnly}
          required
          aria-required="true"
          maxLength={NPC_WHO_MAX}
          placeholder="Une ligne : qui est cette personne, ce que la source porte"
          onChange={(event) => onDraft({ ...draft, who: event.currentTarget.value })}
          help="Une ligne, servie en tête du bloc du NPC."
        />
      </section>

      <section className="space-y-2">
        {/* The package field draws its help UNDER the field: the hint that sat
            between the label and the field now follows it. */}
        <TextArea
          label="Comment jouer"
          data-testid="npc-comment-jouer"
          value={draft.howToPlay}
          readOnly={readOnly}
          required
          aria-required="true"
          maxLength={NPC_HOW_TO_PLAY_MAX}
          rows={6}
          onChange={(event) => onDraft({ ...draft, howToPlay: event.currentTarget.value })}
          help="La consigne propre à ce NPC, après les règles de Réglages > Rejeu. Les réponses aux pages d’une expérience précise se disent ici."
        />
        <Counter
          used={draft.howToPlay.length}
          max={NPC_HOW_TO_PLAY_MAX}
          testId="npc-comment-compteur"
        />
      </section>

      <NpcSourcesEditor draft={draft} readOnly={readOnly} onDraft={onDraft} />

      <fieldset className="min-w-0 space-y-2 border-0 p-0">
        <legend className="mb-1 block font-semibold text-gray-800 text-small">
          Ce qu’il éprouve <span className="font-normal text-gray-600">(facultatif)</span>
        </legend>
        <p className="text-caption text-gray-600 leading-4">
          Les comportements difficiles du banc qu’il met à l’épreuve. Un célèbre n’en a souvent
          aucun : il joue naturellement.
        </p>
        <ul className="flex flex-wrap gap-2 pt-1">
          {NPC_BEHAVIOURS.map((behaviour: NpcBehaviour) => {
            const on = draft.behaviours.includes(behaviour);
            return (
              <li key={behaviour}>
                <button
                  type="button"
                  data-testid={`npc-eprouve-${behaviour}`}
                  aria-pressed={on}
                  disabled={readOnly}
                  onClick={() => onDraft(withBehaviourToggled(draft, behaviour))}
                  className={`h-9 cursor-pointer rounded-full border-2 px-3.5 font-semibold text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-default ${
                    on
                      ? 'border-bleu-nuit bg-bleu-nuit-tint text-bleu-nuit'
                      : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {NPC_BEHAVIOUR_LABELS[behaviour].toLowerCase()}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="pt-2">
          <TextField
            label="Le moment"
            data-testid="npc-moment-champ"
            value={draft.moment}
            readOnly={readOnly}
            maxLength={NPC_MOMENT_MAX}
            onChange={(event) => onDraft({ ...draft, moment: event.currentTarget.value })}
            help="Une ligne sur le moment que la source raconte, lue sur la carte."
          />
        </div>
      </fieldset>

      <fieldset className="min-w-0 space-y-2 border-0 p-0">
        <legend className="mb-1 block font-semibold text-gray-800 text-small">
          Noyau attendu <span className="font-normal text-gray-600">(facultatif)</span>
        </legend>
        <div
          role="radiogroup"
          aria-label="Noyau attendu"
          className="inline-flex max-w-full flex-wrap overflow-hidden rounded-md border-2 border-gray-300"
        >
          {NPC_EXPECTED_OPTIONS.map((option) => {
            const checked = draft.expectedNoyau === option.value;
            return (
              <label
                key={option.label}
                className={`relative inline-flex h-10 min-w-12 items-center justify-center border-gray-300 border-r px-3 font-semibold text-small last:border-r-0 ${
                  checked ? 'bg-bleu-nuit text-white' : 'bg-white text-gray-900'
                } ${readOnly ? '' : 'cursor-pointer hover:bg-gray-50'}`}
              >
                {/* The radio FILLS its segment and is transparent: a hidden input under a label
                    is a control no pointer reaches (the segmented control of the page editor). */}
                <input
                  type="radio"
                  name={expectedName}
                  data-testid={`npc-noyau-${option.value ?? 'aucun'}`}
                  checked={checked}
                  disabled={readOnly && !checked}
                  onChange={() => {
                    if (!readOnly) onDraft({ ...draft, expectedNoyau: option.value });
                  }}
                  className="absolute inset-0 m-0 size-full cursor-pointer opacity-0"
                />
                <span aria-hidden="true">{option.label}</span>
                <span className="sr-only">
                  {option.value === null ? 'Aucun noyau attendu' : option.label}
                </span>
              </label>
            );
          })}
        </div>
        <p className="text-caption text-gray-600 leading-4">
          Affiché et filtrable, jamais comparé à l’écran au noyau retenu.
        </p>
      </fieldset>

      <NpcBlockPanel draft={draft} settings={settings} />
    </div>
  );
}
